import { useEffect, useState } from "react";
import { Plus, Copy, ExternalLink, Calendar as CalendarIcon, Trash2, Pencil, Code, RefreshCw } from "lucide-react";
import { Avatar, Badge, Button, ColorSwatchPicker, Combobox, EmptyState, Input, Label, Modal, ModalFooter, Select, Skeleton, Textarea, useConfirm, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { TIMEZONES } from "@/lib/timezones";
import type { Calendar } from "@/lib/types";

/**
 * Public booking-link management. Each "scheduling link" is a Calendar
 * with a URL slug. The user shares /book/<slug> with customers and they
 * can self-book a time. This page is the management view.
 */
export function SchedulingLinksPage() {
  const toast = useToast();
  const confirm = useConfirm();
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Calendar | "new" | null>(null);
  const [embedFor, setEmbedFor] = useState<Calendar | null>(null);
  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.get<{ calendars: Calendar[] }>("/calendars");
      setCalendars(d.calendars);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);

  const onDelete = async (c: Calendar) => {
    if (!(await confirm({
      title: `Delete "${c.name}"?`,
      description: "Existing appointments will be detached, not deleted. The booking URL will stop working.",
      confirmLabel: "Delete link",
      danger: true,
    }))) return;
    setCalendars((prev) => prev.filter((x) => x.id !== c.id));
    try {
      await api.delete(`/calendars/${c.id}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
      load();
    }
  };

  const copyUrl = (slug: string) => {
    navigator.clipboard.writeText(`${baseUrl}/book/${slug}`);
    toast.success("Copied");
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100 sm:text-2xl">Booking links</h1>
          <p className="mt-1 text-sm text-zinc-500">
            Public links your customers use to book time on your calendar. Share the URL or embed it on a page.
          </p>
        </div>
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing("new")}>
          New booking link
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-40" />)}
        </div>
      ) : calendars.length === 0 ? (
        <EmptyState
          icon={CalendarIcon}
          title="No booking links yet"
          description="Each booking link is a public page (like Calendly) where customers self-book a time. They show up on your Calendar automatically."
          action={<Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing("new")}>Create your first booking link</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {calendars.map((c) => {
            const url = `${baseUrl}/book/${c.slug}`;
            return (
              <div key={c.id} className="group flex flex-col rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-4 transition-colors hover:border-zinc-700">
                <div className="flex items-start justify-between gap-2">
                  <Badge color={c.color}>{c.name}</Badge>
                  <span className="text-xs text-zinc-500">{c.duration_minutes} min</span>
                </div>
                {c.description && <p className="mt-2 text-xs text-zinc-400 line-clamp-2">{c.description}</p>}
                <p className="mt-3 truncate font-mono text-xs text-zinc-500">{url}</p>
                <p className="mt-1 text-[10px] text-zinc-600">
                  {c.timezone}
                  {c.sync_provider && (
                    <span className="ml-2 text-emerald-500/80">
                      · Follows {providerName(c.sync_provider)}{c.sync_calendar_name ? ` (${c.sync_calendar_name})` : ""}
                    </span>
                  )}
                </p>
                <div className="mt-3 flex items-center gap-1 opacity-80 group-hover:opacity-100 transition-opacity">
                  <Button variant="outline" size="sm" leftIcon={<Copy className="h-3.5 w-3.5" />} onClick={() => copyUrl(c.slug)} className="flex-1">
                    Copy link
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => window.open(url, "_blank")} aria-label="Open"><ExternalLink className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => setEmbedFor(c)} aria-label="Embed code"><Code className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => setEditing(c)} aria-label="Edit"><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => onDelete(c)} aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <CalendarEditDialog
          calendar={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {embedFor && (
        <EmbedCodeDialog
          calendar={embedFor}
          baseUrl={baseUrl}
          onClose={() => setEmbedFor(null)}
        />
      )}
    </div>
  );
}

type SyncProvider = "google" | "microsoft";

function providerName(p: string | null | undefined): string {
  return p === "google" ? "Google Calendar" : p === "microsoft" ? "Outlook (Microsoft 365)" : "";
}

interface SyncAccount { provider: SyncProvider; name: string; label: string; username: string | null }
interface RemoteCalendar { id: string; name: string; primary: boolean }

function CalendarEditDialog({
  calendar, onClose, onSaved,
}: { calendar: Calendar | null; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [name, setName] = useState(calendar?.name ?? "");

  // ── Outside calendar (Google Calendar / Outlook) ──
  // One connected account per CRM calendar: its busy time blocks the booking
  // page, and bookings are written into it with a Meet / Teams link.
  const [syncAvailable, setSyncAvailable] = useState(true);
  const [accounts, setAccounts] = useState<SyncAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  const [syncKey, setSyncKey] = useState(
    calendar?.sync_provider ? `${calendar.sync_provider}:${calendar.sync_label ?? "default"}` : "",
  );
  const [remoteCals, setRemoteCals] = useState<RemoteCalendar[]>([]);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [remoteError, setRemoteError] = useState<string | null>(null);
  const [syncCalendarId, setSyncCalendarId] = useState(calendar?.sync_calendar_id ?? "");
  const [syncCalendarName, setSyncCalendarName] = useState(calendar?.sync_calendar_name ?? "");
  const [syncConference, setSyncConference] = useState(calendar?.sync_conference ?? true);
  const [syncInvite, setSyncInvite] = useState(calendar?.sync_invite_contact ?? true);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.get<{ available: boolean; accounts: SyncAccount[] }>("/calendars/sync/accounts")
      .then((d) => { if (cancelled) return; setSyncAvailable(d.available); setAccounts(d.accounts); })
      .catch((err) => { if (!cancelled) { setAccounts([]); setAccountsError(err instanceof ApiError ? err.message : "Could not read your connected accounts right now."); } })
      .finally(() => { if (!cancelled) setAccountsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const [syncProvider, syncLabel] = syncKey ? (syncKey.split(":") as [SyncProvider, string]) : [null, null];

  useEffect(() => {
    if (!syncProvider) { setRemoteCals([]); setRemoteError(null); return; }
    let cancelled = false;
    setRemoteLoading(true);
    setRemoteError(null);
    api.get<{ calendars: RemoteCalendar[] }>("/calendars/sync/remote-calendars", { provider: syncProvider, label: syncLabel ?? "default" })
      .then((d) => {
        if (cancelled) return;
        setRemoteCals(d.calendars);
        setSyncCalendarId((cur) => {
          if (cur && d.calendars.some((c) => c.id === cur)) return cur;
          const primary = d.calendars.find((c) => c.primary) ?? d.calendars[0];
          setSyncCalendarName(primary?.name ?? "");
          return primary?.id ?? "";
        });
      })
      .catch((err) => { if (!cancelled) setRemoteError(err instanceof ApiError ? err.message : "Could not list calendars"); })
      .finally(() => { if (!cancelled) setRemoteLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncKey]);

  const runCheck = async () => {
    if (!calendar) return;
    setChecking(true);
    setCheckResult(null);
    try {
      const r = await api.post<{ ok: boolean; busy_blocks?: number; error?: string }>(`/calendars/${calendar.id}/sync/check`, {});
      setCheckResult(r.ok
        ? { ok: true, text: `Connected. ${r.busy_blocks ?? 0} busy block${r.busy_blocks === 1 ? "" : "s"} found in the next 7 days.` }
        : { ok: false, text: r.error ?? "Could not read the calendar." });
    } catch (err) {
      setCheckResult({ ok: false, text: err instanceof ApiError ? err.message : "Could not read the calendar." });
    } finally { setChecking(false); }
  };
  const [slug, setSlug] = useState(calendar?.slug ?? "");
  const [description, setDescription] = useState(calendar?.description ?? "");
  const [duration, setDuration] = useState(calendar?.duration_minutes ?? 30);
  const [tz, setTz] = useState(calendar?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [color, setColor] = useState(calendar?.color ?? "indigo");
  const [bufferBefore, setBufferBefore] = useState(calendar?.buffer_before_minutes ?? 0);
  const [bufferAfter, setBufferAfter] = useState(calendar?.buffer_after_minutes ?? 0);
  const [minNotice, setMinNotice] = useState(calendar?.min_notice_minutes ?? 60);
  const [maxAdvance, setMaxAdvance] = useState(calendar?.max_advance_days ?? 60);
  const [saving, setSaving] = useState(false);

  // Auto-generate slug from name when creating.
  useEffect(() => {
    if (!calendar) {
      setSlug(name.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""));
    }
  }, [name, calendar]);

  const submit = async () => {
    if (!name.trim() || !slug) return;
    setSaving(true);
    try {
      const body = {
        name: name.trim(), slug,
        description: description.trim() || null,
        duration_minutes: duration,
        timezone: tz,
        color,
        buffer_before_minutes: bufferBefore,
        buffer_after_minutes: bufferAfter,
        min_notice_minutes: minNotice,
        max_advance_days: maxAdvance,
        sync_provider: syncProvider,
        sync_label: syncProvider ? (syncLabel ?? "default") : null,
        sync_calendar_id: syncProvider ? (syncCalendarId || null) : null,
        sync_calendar_name: syncProvider ? (syncCalendarName || null) : null,
        sync_conference: syncConference,
        sync_invite_contact: syncInvite,
      };
      if (calendar) await api.patch(`/calendars/${calendar.id}`, body);
      else          await api.post("/calendars", body);
      toast.success(calendar ? "Saved" : "Created");
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={calendar ? "Edit booking link" : "New booking link"} size="lg">
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div><Label required>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. 30-min Demo" autoFocus /></div>
          <div>
            <Label required>URL slug</Label>
            <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="demo-call" />
          </div>
        </div>
        <div>
          <Label>Description</Label>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Shown to customers on the booking page" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Duration (min)</Label><Input type="number" value={duration} onChange={(e) => setDuration(parseInt(e.target.value, 10) || 30)} /></div>
          <div>
            <Label>Timezone</Label>
            <Combobox
              value={tz}
              onChange={setTz}
              options={(TIMEZONES.includes(tz) ? TIMEZONES : [tz, ...TIMEZONES]).map((z) => ({ value: z }))}
              placeholder="America/Los_Angeles"
            />
          </div>
          <div><Label>Buffer before (min)</Label><Input type="number" value={bufferBefore} onChange={(e) => setBufferBefore(parseInt(e.target.value, 10) || 0)} /></div>
          <div><Label>Buffer after (min)</Label><Input type="number" value={bufferAfter} onChange={(e) => setBufferAfter(parseInt(e.target.value, 10) || 0)} /></div>
          <div><Label>Min notice (min)</Label><Input type="number" value={minNotice} onChange={(e) => setMinNotice(parseInt(e.target.value, 10) || 0)} /></div>
          <div><Label>Max advance (days)</Label><Input type="number" value={maxAdvance} onChange={(e) => setMaxAdvance(parseInt(e.target.value, 10) || 60)} /></div>
        </div>
        <div>
          <Label>Color</Label>
          <ColorSwatchPicker value={color} onChange={setColor} />
        </div>

        <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-zinc-200">Connected calendar</p>
              <p className="mt-0.5 text-xs text-zinc-500">
                Follow one person&rsquo;s Google Calendar or Outlook: times that are busy there are never offered, and every booking is added there with a video call link.
                Only busy/free is read - event titles and notes stay private.
              </p>
            </div>
          </div>
          {!syncAvailable ? (
            <p className="mt-3 text-xs text-zinc-500">Not available in this environment.</p>
          ) : accountsLoading ? (
            <Skeleton className="mt-3 h-9" />
          ) : accountsError ? (
            <p className="mt-3 text-xs text-rose-300/90">{accountsError}{calendar?.sync_provider ? ` Your current link (${providerName(calendar.sync_provider)}) is kept.` : ""}</p>
          ) : accounts.length === 0 ? (
            <p className="mt-3 text-xs text-amber-300/90">
              No Google or Microsoft 365 account is connected yet. Connect one on your SyncSuite <strong>Connections</strong> page (use &ldquo;Add another&rdquo; for each team member), then pick it here.
            </p>
          ) : (
            <div className="mt-3 space-y-3">
              <div>
                <Label>Account</Label>
                <Select value={syncKey} onChange={(e) => { setSyncKey(e.target.value); setSyncCalendarId(""); setSyncCalendarName(""); setCheckResult(null); }}>
                  <option value="">Not linked</option>
                  {accounts.map((a) => (
                    <option key={`${a.provider}:${a.label}`} value={`${a.provider}:${a.label}`}>
                      {providerName(a.provider)} · {a.username ?? a.label}{a.username && a.label !== "default" ? ` (${a.label})` : ""}
                    </option>
                  ))}
                </Select>
              </div>
              {syncProvider && (
                <>
                  <div>
                    <Label>Calendar in that account</Label>
                    {remoteLoading ? (
                      <Skeleton className="h-9" />
                    ) : remoteError ? (
                      <p className="text-xs text-rose-300/90">{remoteError}</p>
                    ) : (
                      <Select
                        value={syncCalendarId}
                        onChange={(e) => {
                          setSyncCalendarId(e.target.value);
                          setSyncCalendarName(remoteCals.find((c) => c.id === e.target.value)?.name ?? "");
                        }}
                      >
                        {remoteCals.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}{c.primary ? " (main)" : ""}</option>
                        ))}
                      </Select>
                    )}
                  </div>
                  <label className="flex items-start gap-2 text-sm text-zinc-300">
                    <input type="checkbox" className="mt-0.5 accent-indigo-500" checked={syncConference} onChange={(e) => setSyncConference(e.target.checked)} />
                    <span>
                      Add a video call link to each booking
                      <span className="block text-xs text-zinc-500">{syncProvider === "google" ? "Google Meet" : "Microsoft Teams (work or school accounts only)"}</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 text-sm text-zinc-300">
                    <input type="checkbox" className="mt-0.5 accent-indigo-500" checked={syncInvite} onChange={(e) => setSyncInvite(e.target.checked)} />
                    <span>
                      Send the contact a calendar invite
                      <span className="block text-xs text-zinc-500">They get the event (and the link) from {providerName(syncProvider)} as well as your CRM&rsquo;s confirmation.</span>
                    </span>
                  </label>
                  {calendar && calendar.sync_provider === syncProvider && (calendar.sync_label ?? "default") === syncLabel && (
                    <div className="flex items-center gap-3">
                      <Button variant="outline" size="sm" leftIcon={<RefreshCw className="h-3.5 w-3.5" />} loading={checking} onClick={runCheck}>
                        Check connection
                      </Button>
                      {checkResult && (
                        <span className={checkResult.ok ? "text-xs text-emerald-400" : "text-xs text-rose-300"}>{checkResult.text}</span>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} loading={saving} disabled={!name.trim() || !slug}>{calendar ? "Save changes" : "Create"}</Button>
      </ModalFooter>
    </Modal>
  );
}

function EmbedCodeDialog({ calendar, baseUrl, onClose }: { calendar: Calendar; baseUrl: string; onClose: () => void }) {
  const toast = useToast();
  const url = `${baseUrl}/book/${calendar.slug}`;
  const iframe = `<iframe src="${url}" width="100%" height="700" frameborder="0" style="border-radius: 12px;"></iframe>`;
  const link = `<a href="${url}" target="_blank">Book a time with us</a>`;

  return (
    <Modal open onClose={onClose} title={`Embed "${calendar.name}"`} size="lg">
      <p className="text-sm text-zinc-400 mb-3">Drop this code into any web page to let visitors book directly.</p>
      <div className="space-y-3">
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label>Iframe (recommended)</Label>
            <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(iframe); toast.success("Copied"); }} leftIcon={<Copy className="h-3 w-3" />}>Copy</Button>
          </div>
          <pre className="rounded-lg border border-zinc-800 bg-zinc-950 p-3 text-xs text-zinc-300 font-mono overflow-x-auto whitespace-pre-wrap">{iframe}</pre>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label>Simple link</Label>
            <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(link); toast.success("Copied"); }} leftIcon={<Copy className="h-3 w-3" />}>Copy</Button>
          </div>
          <pre className="rounded-lg border border-zinc-800 bg-zinc-950 p-3 text-xs text-zinc-300 font-mono overflow-x-auto whitespace-pre-wrap">{link}</pre>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label>Direct URL</Label>
            <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(url); toast.success("Copied"); }} leftIcon={<Copy className="h-3 w-3" />}>Copy</Button>
          </div>
          <pre className="rounded-lg border border-zinc-800 bg-zinc-950 p-3 text-xs text-zinc-300 font-mono overflow-x-auto">{url}</pre>
        </div>
      </div>
      <ModalFooter>
        <Button onClick={onClose}>Done</Button>
      </ModalFooter>
    </Modal>
  );
}
